// frontend/my-react-app/src/PetSwitcher.jsx
// Header dropdown: current pet, list of pets, Settings (placeholder), Log out.
// Updated: 2026-03-10 — visual tweaks: icons for Settings/Log out, clearer active state.
// Updated: 2026-03-10 — hardcoded 3 colors (index-based) for distinct pet avatars.
import React, { useState, useRef, useEffect } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { usePet } from './contexts/PetContext';

const PetSwitcher = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { signOut } = useClerk();
  const { pets, selectedPet, switchPet, isLoading } = usePet();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePetSwitch = (petId) => {
    switchPet(petId);
    setIsDropdownOpen(false);
  };

  const handleSettings = () => {
    setIsDropdownOpen(false);
    // Placeholder for pilot (DEC-015)
    alert('Settings coming soon');
  };

  if (isLoading) {
    return (
      <div className="profile-section">
        <div className="profile-button" style={{ cursor: 'default' }}>
          <span className="profile-initial-small" style={{ backgroundColor: '#9CA3AF' }}>
            ...
          </span>
        </div>
      </div>
    );
  }

  if (pets.length === 0) {
    return (
      <div className="profile-section" ref={dropdownRef}>
        <div className="profile-button" style={{ cursor: 'default' }}>
          <span className="profile-initial-small" style={{ backgroundColor: '#9CA3AF' }}>?</span>
          <span className="profile-button-text">No pets</span>
        </div>
      </div>
    );
  }

  const initial = selectedPet?.name?.charAt(0).toUpperCase() || '?';
  const colors = ['#3B82F6', '#059669', '#8B5CF6'];
  const getColorForIndex = (index) => colors[Math.min(index, colors.length - 1)];
  const selectedIndex = pets.findIndex((p) => p.id === selectedPet?.id);
  const avatarColor = selectedIndex >= 0 ? getColorForIndex(selectedIndex) : colors[0];
  const showDropdown = pets.length > 1;

  return (
    <div className="profile-section" ref={dropdownRef}>
      <button
        className="profile-button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-expanded={isDropdownOpen}
      >
        <span
          className="profile-initial-small"
          style={{ backgroundColor: avatarColor }}
        >
          {initial}
        </span>
        <span className="profile-button-text">{selectedPet?.name ?? 'Pet'}</span>
        {(showDropdown || isDropdownOpen) && (
          <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
        )}
      </button>

      {isDropdownOpen && (
        <div className="dropdown">
          {showDropdown && (
            <>
              <div className="dropdown-header">Pets</div>
              <div className="dropdown-divider" />
              {pets.map((pet, index) => {
                const petInitial = pet.name?.charAt(0).toUpperCase() || '?';
                const petColor = getColorForIndex(index);
                const isSelected = pet.id === selectedPet?.id;
                return (
                  <button
                    key={pet.id}
                    className={`dropdown-item ${isSelected ? 'active' : ''}`}
                    onClick={() => handlePetSwitch(pet.id)}
                  >
                    <span
                      className="profile-initial"
                      style={{ backgroundColor: petColor }}
                    >
                      {petInitial}
                    </span>
                    <span className="profile-name">{pet.name}</span>
                    {isSelected && <span className="dropdown-item-check" aria-hidden="true">✓</span>}
                  </button>
                );
              })}
              <div className="dropdown-divider" />
            </>
          )}
          <button className="dropdown-item" onClick={handleSettings}>
            <span className="dropdown-item-icon" aria-hidden="true">⚙</span>
            <span>Settings</span>
          </button>
          <div className="dropdown-divider" />
          <button className="dropdown-item logout" onClick={() => signOut()}>
            <span className="dropdown-item-icon" aria-hidden="true">→</span>
            <span>Log out</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default PetSwitcher;
